import React, { useRef, useState, useMemo, forwardRef, useEffect } from 'react';
import { useSlateStatic } from 'slate-react';
import classnames from 'classnames';

import Show from 'components/Show';

const Spellcheck = forwardRef(({ element, children, ...attributes }, ref) => {
	// Get editor's instance
	const editor = useSlateStatic();

	// Check if word is initialized
	const initialized = useRef(false);

	// Store editors mode
	const mode = useMemo(() => editor.spellcheckMode, [editor.spellcheckMode]);

	// Store spellcheck's value
	const [data, setData] = useState({
		value: '',
		answer: '',
		correct: false
	});

	// Handle spellcheck's change
	// @event: object | default onChange event
	const onChange = (event) => {
		const { name, value } = event.target;

		// Update spellcheck edit state
		if (mode === 'edit') {
			setData(prev => ({
				...prev,
				value: value,
			}));
		}

		// Update spellcheck answer state
		if (mode === 'answer') {
			setData(prev => ({
				...prev,
				answer: value,
			}));
		}

		// Update base component's value
		editor.onWordChange({ name, value });
	};

	useEffect(() => {
		// Do not reinitialize
		if (initialized.current) return;

		// Find original word
		const original = editor.words?.find(word => word.key === element.name);

		// Do not initialize if word doesnt exist YET
		if (!original) return;

		// Initialize word
		if (mode === 'edit') {
			setData(prev => ({
				...prev,
				value: original.value
			}));
		}

		if (mode === 'answer') {
			setData(prev => ({
				...prev,
				answer: original.answer
			}));
		}

		if (mode === 'view') {
			setData(prev => ({
				...prev,
				value: original.value
			}));
		}

		if (mode === 'review') {
			setData(prev => ({
				...prev,
				value: original.value,
				answer: original.answer,
				correct: original.correct
			}));
		}

		// Update initialization state
		initialized.current = true;
	}, [editor.words]);

	const inputClassName = classnames(
		'absolute min-w-[1rem] bottom-1 w-full appearance-none py-0 px-1 leading-none rounded transition-colors',
		(mode === 'answer' && data.answer === '') && 'bg-amber-500',
		(mode === 'answer' && data.answer !== '') && 'bg-neutral-100 dark:bg-neutral-700',
		mode === 'edit' && 'bg-neutral-100 dark:bg-neutral-700'
	);
		
	return (
		<span
			ref={ref}
			{...attributes} 
			contentEditable={false}
		>
			{/* Edit or answer mode */}
			<Show if={mode === 'edit' || mode === 'answer'}>
				<span className="relative inline-block max-w-full">
					{/* Word input field */}
					<input
						type="text"
						name={element.name}
						value={mode === 'edit' ? data.value : data.answer}
						onChange={onChange}
						className={inputClassName}
					/>

					{/* Placeholder for autosizing input */}
					<span className="inline-block px-1 min-w-[1rem] invisible">{mode === 'edit' ? data.value : data.answer}</span>
				</span>
			</Show>

			{/* Review mode correct */}
			<Show if={mode === 'review' && data.correct}>
				<span className="py-0 px-1 leading-none rounded bg-emerald-500 text-white">
					{data.answer}
				</span>
			</Show>

			{/* Review mode incorrect */}
			<Show if={mode === 'review' && !data.correct}>
				<span className="py-0 px-1 leading-none rounded bg-red-500 text-white">
					<del>{data.answer}</del> - {data.value}
				</span>
			</Show>

			{/* View mode */}
			<Show if={mode === 'view'}>
				<span className="py-0 px-1 leading-none rounded bg-neutral-100 dark:bg-neutral-700 transition-colors">
					{data.value}
				</span>
			</Show>

			{children}
		</span>
	);
});

export default Spellcheck;
